import { useState, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import {
  useGetSchoolMergeSuggestions,
  useConfirmSchoolMerge,
  useUndoSchoolMerge,
  useListSchoolMergeLog,
  getGetSchoolMergeSuggestionsQueryKey,
  getListSchoolMergeLogQueryKey,
} from "@workspace/api-client-react";
import type {
  MergeSuggestion,
  MergeCandidate,
  SchoolMergeLogEntry,
  SchoolMergeLogPair,
  Profile,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  CheckCircle2, AlertTriangle, HelpCircle, Loader2, ArrowRight, Search, Undo2, ExternalLink,
} from "lucide-react";
import { useGetProfile, useUpdateProfile, getGetProfileQueryKey } from "@workspace/api-client-react";

// Count how many records this pair actually re-linked from the private
// student to the school student. We use the same "newly-added school id"
// rule the per-pair undo uses, so the number the user sees here matches
// what would actually be reverted if they clicked Undo.
function recordsMovedForPair(p: SchoolMergeLogPair): number {
  // Older log rows (predating the recordSnapshots field) may have no
  // snapshots at all. Treat missing/empty as 0 rather than throwing.
  const snaps = p.recordSnapshots ?? [];
  let n = 0;
  for (const snap of snaps) {
    if (snap.after.includes(p.schoolStudentId) && !snap.before.includes(p.schoolStudentId)) {
      n++;
    }
  }
  return n;
}

function profilePreferences(profile: Profile | undefined): Record<string, unknown> {
  const prefs = profile?.preferences;
  return prefs && typeof prefs === "object" ? (prefs as Record<string, unknown>) : {};
}

type Decision =
  | { kind: "skip" }
  | { kind: "confirm"; schoolStudentId: number };

function ConfidenceBadge({ confidence }: { confidence: MergeSuggestion["confidence"] }) {
  if (confidence === "high") {
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
        <CheckCircle2 className="h-3 w-3 mr-1" /> Strong match
      </Badge>
    );
  }
  if (confidence === "probable") {
    return (
      <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">
        <HelpCircle className="h-3 w-3 mr-1" /> Probable
      </Badge>
    );
  }
  if (confidence === "manual") {
    return (
      <Badge className="bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100">
        <AlertTriangle className="h-3 w-3 mr-1" /> Choose manually
      </Badge>
    );
  }
  return (
    <Badge className="bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100">
      No suggestion
    </Badge>
  );
}

function RowEditor({
  suggestion,
  schoolRoster,
  decision,
  onChange,
  takenByOthers,
}: {
  suggestion: MergeSuggestion;
  schoolRoster: MergeCandidate[];
  decision: Decision | undefined;
  onChange: (d: Decision | undefined) => void;
  // School student ids already chosen by OTHER rows in this same wizard
  // session. Picking the same id from two rows would create a many-to-one
  // mapping that breaks per-link undo, so we disable those options.
  takenByOthers: Set<number>;
}) {
  const [search, setSearch] = useState("");
  // Lets the user pop open the full-roster picker even on high/probable rows
  // so an incorrect AI suggestion is never a dead end.
  const [changingMatch, setChangingMatch] = useState(false);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? schoolRoster.filter((s) => `${s.firstName} ${s.lastName ?? ""}`.toLowerCase().includes(q))
      : schoolRoster;
    return list.slice(0, 50);
  }, [schoolRoster, search]);

  const showCandidatePicker =
    suggestion.confidence === "manual" || suggestion.candidates.length > 1;
  // Show the searchable full-roster picker for: manual, no-suggestion, or when
  // the user explicitly clicked "Change match" on a high/probable row.
  const showRosterSearch =
    suggestion.confidence === "manual" ||
    suggestion.confidence === "none" ||
    changingMatch;

  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-3" data-testid={`row-merge-${suggestion.privateStudentId}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            {`${suggestion.firstName} ${suggestion.lastName ?? ""}`.trim()}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {suggestion.yearLevel ? `Year ${suggestion.yearLevel}` : "No year level"}
            {" · "}
            {suggestion.recordCount ?? 0} record{(suggestion.recordCount ?? 0) === 1 ? "" : "s"}
          </p>
        </div>
        <ConfidenceBadge confidence={suggestion.confidence} />
      </div>

      {suggestion.confidence !== "none" && !showCandidatePicker && suggestion.candidates[0] && (
        <div className="flex items-center gap-2 bg-gray-50 rounded-md px-3 py-2">
          <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <div className="text-sm text-gray-700 min-w-0">
            <span className="font-medium">
              {`${suggestion.candidates[0].firstName} ${suggestion.candidates[0].lastName ?? ""}`.trim()}
            </span>
            {suggestion.candidates[0].yearLevel && (
              <span className="text-gray-500 text-xs ml-2">
                Year {suggestion.candidates[0].yearLevel}
              </span>
            )}
          </div>
        </div>
      )}

      {showCandidatePicker && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-700">
            {suggestion.confidence === "manual"
              ? `Multiple matches found — pick the right student.`
              : "Choose the matching school student."}
          </p>
          {suggestion.candidates.map((c) => {
            const taken = takenByOthers.has(c.schoolStudentId);
            return (
              <label
                key={c.schoolStudentId}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border ${
                  taken
                    ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                    : decision?.kind === "confirm" && decision.schoolStudentId === c.schoolStudentId
                    ? "border-primary bg-primary/5 cursor-pointer"
                    : "border-gray-200 hover:bg-gray-50 cursor-pointer"
                }`}
              >
                <input
                  type="radio"
                  name={`row-${suggestion.privateStudentId}`}
                  checked={decision?.kind === "confirm" && decision.schoolStudentId === c.schoolStudentId}
                  disabled={taken}
                  onChange={() =>
                    onChange({ kind: "confirm", schoolStudentId: c.schoolStudentId })
                  }
                  data-testid={`radio-candidate-${suggestion.privateStudentId}-${c.schoolStudentId}`}
                />
                <span className="text-sm text-gray-800">
                  {`${c.firstName} ${c.lastName ?? ""}`.trim()}
                </span>
                {c.yearLevel && (
                  <span className="text-xs text-gray-500 ml-auto">Year {c.yearLevel}</span>
                )}
                {taken && (
                  <span className="text-[11px] text-gray-500 ml-2">Already chosen above</span>
                )}
              </label>
            );
          })}
        </div>
      )}

      {showRosterSearch && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-700">Search the full school roster</p>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type a name…"
              className="pl-8 h-9 text-sm"
              data-testid={`input-search-${suggestion.privateStudentId}`}
            />
          </div>
          {search && (
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md divide-y">
              {filtered.length === 0 ? (
                <p className="text-xs text-gray-400 px-3 py-2">No matches.</p>
              ) : (
                filtered.map((s) => {
                  const taken = takenByOthers.has(s.schoolStudentId);
                  return (
                    <button
                      key={s.schoolStudentId}
                      type="button"
                      disabled={taken}
                      onClick={() => {
                        onChange({ kind: "confirm", schoolStudentId: s.schoolStudentId });
                        setChangingMatch(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
                        taken
                          ? "opacity-50 cursor-not-allowed bg-gray-50"
                          : "hover:bg-gray-50"
                      } ${
                        !taken &&
                        decision?.kind === "confirm" &&
                        decision.schoolStudentId === s.schoolStudentId
                          ? "bg-primary/5"
                          : ""
                      }`}
                      data-testid={`button-pick-${suggestion.privateStudentId}-${s.schoolStudentId}`}
                    >
                      <span className="text-gray-800">
                        {`${s.firstName} ${s.lastName ?? ""}`.trim()}
                      </span>
                      {s.yearLevel && (
                        <span className="text-gray-400 ml-auto">Year {s.yearLevel}</span>
                      )}
                      {taken && (
                        <span className="text-[11px] text-gray-400">already chosen</span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1 flex-wrap">
        {(suggestion.confidence === "high" || suggestion.confidence === "probable") &&
          !showCandidatePicker &&
          suggestion.candidates[0] && (
            <Button
              size="sm"
              variant={
                decision?.kind === "confirm" &&
                decision.schoolStudentId === suggestion.candidates[0].schoolStudentId
                  ? "default"
                  : "outline"
              }
              onClick={() =>
                onChange({
                  kind: "confirm",
                  schoolStudentId: suggestion.candidates[0].schoolStudentId,
                })
              }
              data-testid={`button-confirm-${suggestion.privateStudentId}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{" "}
              {suggestion.confidence === "probable"
                ? "Confirm probable match"
                : "Confirm match"}
            </Button>
          )}
        {/* Change match: lets the user override an incorrect high/probable
            suggestion by opening the full searchable roster, so no row is ever
            stuck on an unwanted suggestion. */}
        {(suggestion.confidence === "high" || suggestion.confidence === "probable") &&
          !showCandidatePicker && (
            <Button
              size="sm"
              variant={changingMatch ? "secondary" : "ghost"}
              onClick={() => setChangingMatch((v) => !v)}
              data-testid={`button-change-${suggestion.privateStudentId}`}
            >
              <Search className="h-3.5 w-3.5 mr-1" />{" "}
              {changingMatch ? "Cancel change" : "Change match"}
            </Button>
          )}
        <Button
          size="sm"
          variant={decision?.kind === "skip" ? "secondary" : "ghost"}
          onClick={() => onChange(decision?.kind === "skip" ? undefined : { kind: "skip" })}
          data-testid={`button-skip-${suggestion.privateStudentId}`}
        >
          Skip for now
        </Button>
        {decision?.kind === "confirm" && (
          <span className="text-xs text-green-700 ml-auto inline-flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" /> Linked
          </span>
        )}
      </div>
    </div>
  );
}

export function MergeWizard({
  open,
  onClose,
  onCompleted,
}: {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { data, isLoading } = useGetSchoolMergeSuggestions({
    query: { enabled: open },
  });
  const { data: logEntries = [] } = useListSchoolMergeLog({
    query: { enabled: open },
  });
  const confirmMerge = useConfirmSchoolMerge();
  const undoMerge = useUndoSchoolMerge();
  const updateProfile = useUpdateProfile();
  const { data: profile } = useGetProfile();

  const suggestions = data?.suggestions ?? [];
  const schoolRoster = data?.schoolStudents ?? [];

  const [decisions, setDecisions] = useState<Record<number, Decision | undefined>>({});
  // After a successful merge we replace the suggestion editor with a
  // success summary built from the log entry the API just returned. The
  // entry already contains per-pair recordSnapshots so we don't need a
  // refetch to render the per-pair record counts.
  const [successEntry, setSuccessEntry] = useState<SchoolMergeLogEntry | null>(null);
  // Track whether this open of the wizard finished an apply/skip so we
  // can fire onCompleted *after* the modal actually closes — not the
  // moment we apply. That prevents the chained fresh-start wizard from
  // popping while the merge success summary is still on screen.
  const completedThisSessionRef = useRef(false);

  function handleClose() {
    // Reset the success step on close so reopening the wizard doesn't
    // surface stale per-pair counts from a previous session.
    setSuccessEntry(null);
    setDecisions({});
    const fireCompleted = completedThisSessionRef.current;
    completedThisSessionRef.current = false;
    onClose();
    if (fireCompleted) onCompleted?.();
  }

  function handleViewRecords(schoolStudentId: number) {
    handleClose();
    navigate(`/app/records?studentId=${schoolStudentId}`);
  }

  const confirmedPairs = useMemo(
    () =>
      Object.entries(decisions)
        .filter(([, d]) => d?.kind === "confirm")
        .map(([priv, d]) => ({
          privateStudentId: parseInt(priv),
          schoolStudentId: (d as { kind: "confirm"; schoolStudentId: number }).schoolStudentId,
        })),
    [decisions],
  );

  async function handleApply() {
    if (confirmedPairs.length === 0) {
      // Mark wizard completed without changes
      await updateProfile.mutateAsync({
        data: {
          preferences: {
            ...profilePreferences(profile),
            mergeWizardStatus: "completed",
          },
        },
      });
      qc.invalidateQueries({ queryKey: getGetProfileQueryKey() });
      toast({ title: "Skipped — no pairs confirmed." });
      // Skip path closes immediately; mark this session as completed so
      // handleClose's onCompleted hook fires for the chained fresh-start.
      completedThisSessionRef.current = true;
      handleClose();
      return;
    }
    try {
      const entry = await confirmMerge.mutateAsync({ data: { pairs: confirmedPairs } });
      await updateProfile.mutateAsync({
        data: {
          preferences: {
            ...profilePreferences(profile),
            mergeWizardStatus: "completed",
          },
        },
      });
      qc.invalidateQueries();
      // Stay open and show the success summary built from the API response
      // so the user gets per-pair record counts before closing the wizard.
      setSuccessEntry(entry);
      setDecisions({});
      toast({
        title: `Linked ${confirmedPairs.length} student${confirmedPairs.length === 1 ? "" : "s"}.`,
        description: "You can undo from Account settings within 48 hours.",
      });
      // Defer onCompleted until handleClose runs — the user is still
      // looking at the success summary; firing now would pop the
      // chained fresh-start wizard on top of it.
      completedThisSessionRef.current = true;
    } catch (err: unknown) {
      toast({
        title: "Merge failed",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  async function handleUndo(entryId: number, privateStudentId?: number) {
    try {
      await undoMerge.mutateAsync({
        data: privateStudentId !== undefined
          ? { logId: entryId, privateStudentId }
          : { logId: entryId },
      });
      qc.invalidateQueries();
      toast({
        title: privateStudentId !== undefined ? "Link reversed." : "Merge reversed.",
      });
    } catch (err: unknown) {
      toast({
        title: "Undo failed",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {successEntry
              ? "Merge complete"
              : "Link your private students to the school roster"}
          </DialogTitle>
          <DialogDescription>
            {successEntry
              ? "Here's exactly what changed. You have 48 hours to undo any link from this batch."
              : "We've matched your existing students against the school's master list. Confirm each suggestion to keep your historical diary entries and records linked. Nothing merges automatically — you control every link."}
          </DialogDescription>
        </DialogHeader>

        {successEntry ? (
          <div className="space-y-3" data-testid="merge-success-summary">
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-md px-3 py-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-medium text-green-800">
                  Linked {successEntry.pairs.length} student
                  {successEntry.pairs.length === 1 ? "" : "s"} to the school roster.
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Each private student was archived. Their historical records were
                  re-linked to the matching school student so nothing was lost.
                </p>
              </div>
            </div>

            <ul className="divide-y border border-gray-200 rounded-md overflow-hidden">
              {successEntry.pairs.map((p) => {
                const moved = recordsMovedForPair(p);
                return (
                  <li
                    key={p.privateStudentId}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                    data-testid={`merge-success-pair-${p.privateStudentId}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-gray-800 truncate">
                        <span className="font-medium">{p.privateName}</span>
                        <ArrowRight className="inline h-3 w-3 mx-1 text-gray-400" />
                        <span>{p.schoolName}</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {moved} record{moved === 1 ? "" : "s"} re-linked
                        {" · private student archived"}
                      </p>
                    </div>
                    {moved > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => handleViewRecords(p.schoolStudentId)}
                        data-testid={`button-view-records-${p.privateStudentId}`}
                        title={`Open the records list filtered to ${p.schoolName}`}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" /> View
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : suggestions.length === 0 ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900">Nothing to merge.</p>
            <p className="text-xs text-gray-500 mt-1">
              All your students are already linked to the school roster.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions.map((s) => {
              // Build a per-row "taken by others" set so we can disable any
              // school student already chosen by a different row. This blocks
              // many-to-one mappings before they can be POSTed.
              const takenByOthers = new Set<number>();
              for (const [privId, d] of Object.entries(decisions)) {
                if (parseInt(privId) === s.privateStudentId) continue;
                if (d?.kind === "confirm") takenByOthers.add(d.schoolStudentId);
              }
              return (
                <RowEditor
                  key={s.privateStudentId}
                  suggestion={s}
                  schoolRoster={schoolRoster}
                  decision={decisions[s.privateStudentId]}
                  takenByOthers={takenByOthers}
                  onChange={(d) =>
                    setDecisions((prev) => ({ ...prev, [s.privateStudentId]: d }))
                  }
                />
              );
            })}
          </div>
        )}

        {!successEntry && logEntries.filter((e: SchoolMergeLogEntry) => !e.undone).length > 0 && (
          <div className="border-t pt-4 mt-4 space-y-3">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Recent merges (48h undo window)
            </p>
            {logEntries
              .filter((e: SchoolMergeLogEntry) => !e.undone)
              .map((e: SchoolMergeLogEntry) => {
                const activePairs = e.pairs.filter((p) => !p.undone);
                if (activePairs.length === 0) return null;
                return (
                  <div
                    key={e.id}
                    className="border border-gray-200 rounded-md overflow-hidden"
                    data-testid={`merge-log-${e.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 px-3 py-2 bg-gray-50 border-b">
                      <p className="text-xs text-gray-700">
                        {activePairs.length} active link
                        {activePairs.length === 1 ? "" : "s"} ·{" "}
                        {new Date(e.createdAt).toLocaleString()}
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleUndo(e.id)}
                        disabled={undoMerge.isPending}
                        data-testid={`button-undo-${e.id}`}
                        title="Undo every remaining link in this batch"
                      >
                        <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo all
                      </Button>
                    </div>
                    <ul className="divide-y">
                      {activePairs.map((p) => {
                        const moved = recordsMovedForPair(p);
                        return (
                          <li
                            key={p.privateStudentId}
                            className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs"
                            data-testid={`merge-log-pair-${e.id}-${p.privateStudentId}`}
                          >
                            <div className="min-w-0 flex-1">
                              <span className="text-gray-700 truncate block">
                                <span className="font-medium">{p.privateName}</span>
                                <span className="text-gray-400 mx-1">→</span>
                                <span>{p.schoolName}</span>
                              </span>
                              <span className="text-[11px] text-gray-500">
                                {moved} record{moved === 1 ? "" : "s"} re-linked
                              </span>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {moved > 0 && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2"
                                  onClick={() => handleViewRecords(p.schoolStudentId)}
                                  data-testid={`button-view-pair-${e.id}-${p.privateStudentId}`}
                                  title={`Open the records list filtered to ${p.schoolName}`}
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" /> View
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2"
                                onClick={() => handleUndo(e.id, p.privateStudentId)}
                                disabled={undoMerge.isPending}
                                data-testid={`button-undo-pair-${e.id}-${p.privateStudentId}`}
                              >
                                <Undo2 className="h-3 w-3 mr-1" /> Undo this link
                              </Button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
          </div>
        )}

        <DialogFooter className="gap-2">
          {successEntry ? (
            <Button
              onClick={handleClose}
              data-testid="button-merge-success-done"
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={handleClose}
                disabled={confirmMerge.isPending}
                data-testid="button-merge-close"
              >
                Close
              </Button>
              <Button
                onClick={handleApply}
                disabled={confirmMerge.isPending || isLoading}
                data-testid="button-merge-apply"
              >
                {confirmMerge.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                {confirmedPairs.length > 0
                  ? `Apply ${confirmedPairs.length} link${confirmedPairs.length === 1 ? "" : "s"}`
                  : "Skip all & finish"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
